import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus, CalendarDays, CalendarRange, Clock, Users, Copy } from "lucide-react";
import { Layout } from "../../components/layout/Layout";
import { Button } from "../../components/ui/button";
import { MonthCalendar } from "../../components/planning/MonthCalendar";
import { DayTimeline } from "../../components/planning/DayTimeline";
import { AddSessionDialog, SessionDialog } from "../../components/planning/SessionDialogs";
import { DuplicateDialog } from "../../components/planning/DuplicateDialog";
import { StatusChip, EmptyState } from "../../components/common";
import { api } from "../../lib/api";
import { useAuth, hasPerm } from "../../context/AuthContext";
import { cn } from "../../lib/utils";
import { monthGrid, todayISO, addDays, parseISODate, fmtDateLong, JOURS_COURTS, sessionStatus } from "../../lib/format";

export default function PlanningPage() {
  const { user } = useAuth();
  const canManage = hasPerm(user, "planning", "manage");
  const [params, setParams] = useSearchParams();
  const selected = params.get("date");
  const initial = selected ? parseISODate(selected) : new Date();
  const [ym, setYm] = useState({ y: initial.getFullYear(), m: initial.getMonth() });
  const [sessions, setSessions] = useState([]);
  const [modules, setModules] = useState([]);
  const [eleves, setEleves] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addTime, setAddTime] = useState("09:00");
  const [current, setCurrent] = useState(null);
  const [dupOpen, setDupOpen] = useState(false);

  // Plage chargée : toute la grille du mois affiché + la semaine du jour ouvert.
  const range = useMemo(() => {
    const grid = monthGrid(ym.y, ym.m);
    let start = grid[0].iso;
    let end = grid[grid.length - 1].iso;
    if (selected) {
      const d = parseISODate(selected);
      const monday = addDays(selected, -((d.getDay() + 6) % 7));
      if (monday < start) start = monday;
      if (addDays(monday, 6) > end) end = addDays(monday, 6);
    }
    return { start, end };
  }, [ym, selected]);

  const load = useCallback(async () => {
    const { data } = await api.get("/planning/sessions", { params: range });
    setSessions(data);
    return data;
  }, [range]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/modules").then(({ data }) => setModules(data)).catch(() => setModules([]));
    api.get("/eleves").then(({ data }) => setEleves(data)).catch(() => setEleves([]));
  }, []);

  const selectDay = (iso) => {
    const d = parseISODate(iso);
    setYm({ y: d.getFullYear(), m: d.getMonth() });
    setParams({ date: iso });
  };
  const backToMonth = () => setParams({});

  const daySessions = sessions.filter((s) => s.date === selected);
  const week = useMemo(() => {
    if (!selected) return [];
    const d = parseISODate(selected);
    const monday = addDays(selected, -((d.getDay() + 6) % 7));
    return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
  }, [selected]);

  const openAdd = (time) => { setAddTime(time || "09:00"); setAddOpen(true); };
  const onChanged = async (updated) => {
    const data = await load();
    if (updated && current) setCurrent(data.find((s) => s.id === current.id) || null);
  };

  return (
    <Layout
      title="Planning"
      actions={canManage && selected ? (
        <>
          {daySessions.length > 0 && (
            <Button variant="outline" onClick={() => setDupOpen(true)} data-testid="planning-duplicate-day"><Copy size={16} /> <span className="hidden sm:inline">Dupliquer la journée</span></Button>
          )}
          <Button onClick={() => openAdd()} data-testid="planning-add-button"><Plus size={16} /> <span className="hidden sm:inline">Ajouter un module</span></Button>
        </>
      ) : null}
    >
      {!selected ? (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Cliquez sur une date pour l'ouvrir en vue journée et y ajouter des cours.</p>
          <MonthCalendar
            year={ym.y}
            month={ym.m}
            onMonthChange={(y, m) => setYm({ y, m })}
            sessions={sessions}
            selected={selected}
            onSelect={selectDay}
          />
        </div>
      ) : (
        <div className="space-y-4 animate-in fade-in zoom-in-95 duration-200" data-testid="planning-day-view">
          <div className="rounded-xl border border-border bg-card">
            <div className="flex flex-wrap items-center justify-between gap-2 px-3 sm:px-4 py-3 border-b border-border">
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={backToMonth} data-testid="planning-back-month"><CalendarRange size={15} /> Mois</Button>
                <Button variant="ghost" size="icon" onClick={() => selectDay(addDays(selected, -1))} aria-label="Jour précédent"><ChevronLeft /></Button>
                <h2 className="font-heading text-lg font-semibold capitalize px-1">{fmtDateLong(selected)}</h2>
                <Button variant="ghost" size="icon" onClick={() => selectDay(addDays(selected, 1))} aria-label="Jour suivant"><ChevronRight /></Button>
              </div>
              <div className="flex items-center gap-2">
                <StatusChip statut={sessionStatus(selected)} />
                {selected !== todayISO() && <Button variant="outline" size="sm" onClick={() => selectDay(todayISO())}>Aujourd'hui</Button>}
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1 p-2">
              {week.map((iso, i) => {
                const n = sessions.filter((s) => s.date === iso).length;
                const d = parseISODate(iso);
                return (
                  <button
                    key={iso}
                    onClick={() => selectDay(iso)}
                    className={cn(
                      "rounded-lg py-1.5 text-center transition-colors",
                      iso === selected ? "bg-primary text-primary-foreground" : "hover:bg-accent",
                    )}
                  >
                    <div className={cn("text-[10px] uppercase tracking-wide", iso === selected ? "opacity-80" : "text-muted-foreground")}>{JOURS_COURTS[i]}</div>
                    <div className={cn("text-base font-semibold tabular-nums", iso === todayISO() && iso !== selected && "text-primary")}>{d.getDate()}</div>
                    <div className={cn("text-[10px]", iso === selected ? "opacity-80" : "text-muted-foreground")}>{n ? `${n} cours` : "—"}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid gap-4 xl:grid-cols-[1fr_320px]">
            <div className="rounded-xl border border-border bg-card p-3 sm:p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium flex items-center gap-1.5"><Clock size={15} className="text-muted-foreground" /> Timeline de la journée</p>
                {canManage && <p className="text-xs text-muted-foreground hidden sm:block">Astuce : cliquez sur une heure vide pour y placer un module</p>}
              </div>
              <DayTimeline
                date={selected}
                sessions={daySessions}
                canAdd={canManage}
                onSlotClick={openAdd}
                onSessionClick={(s) => setCurrent(s)}
              />
            </div>
            <div className="rounded-xl border border-border bg-card">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <p className="font-heading font-semibold">Cours du jour</p>
                <span className="text-xs text-muted-foreground">{daySessions.length}</span>
              </div>
              {daySessions.length === 0 ? (
                <EmptyState
                  icon={CalendarDays}
                  title="Aucun cours ce jour"
                  text={canManage ? "Ajoutez un module pour créer une plage de cours." : undefined}
                  action={canManage ? <Button size="sm" onClick={() => openAdd()}><Plus size={14} /> Ajouter un module</Button> : null}
                />
              ) : (
                <ul className="divide-y divide-border">
                  {daySessions.map((s) => (
                    <li key={s.id}>
                      <button onClick={() => setCurrent(s)} className="w-full text-left px-4 py-3 hover:bg-accent/60" data-testid={`day-list-session-${s.id}`}>
                        <p className="text-sm font-semibold">{s.module_nom}</p>
                        <p className="text-xs text-muted-foreground tabular-nums">{s.heure_debut} – {s.heure_fin}{s.formateur_nom ? ` · ${s.formateur_nom}` : ""}</p>
                        <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Users size={12} /> {s.eleves?.length || 0} élève{(s.eleves?.length || 0) > 1 ? "s" : ""}</p>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      <AddSessionDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        date={selected}
        defaultTime={addTime}
        modules={modules}
        eleves={eleves}
        currentUser={user}
        onCreated={() => load()}
      />
      <DuplicateDialog
        open={dupOpen}
        onOpenChange={setDupOpen}
        mode="day"
        sourceDate={selected}
        nbCours={daySessions.length}
        onDone={() => load()}
      />
      <SessionDialog
        open={!!current}
        onOpenChange={(o) => !o && setCurrent(null)}
        session={current}
        canManage={canManage}
        modules={modules}
        eleves={eleves}
        onChanged={onChanged}
      />
    </Layout>
  );
}
